export const ASSET_STATUSES = [
  "pending_acceptance",
  "in_stock",
  "assigned",
  "borrowed",
  "return_pending",
  "repairing",
  "pending_disposal",
  "disposed",
  "lost",
] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const ASSET_ACTIONS = [
  "accept",
  "assign",
  "borrow",
  "return",
  "complete_return",
  "start_repair",
  "finish_repair",
  "retire",
  "dispose",
  "report_lost",
  "recover_lost",
] as const;
export type AssetAction = (typeof ASSET_ACTIONS)[number];

const ASSET_TRANSITIONS: Record<AssetAction, { from: AssetStatus[]; to: AssetStatus }> = {
  accept: { from: ["pending_acceptance"], to: "in_stock" },
  assign: { from: ["in_stock"], to: "assigned" },
  borrow: { from: ["in_stock"], to: "borrowed" },
  return: { from: ["assigned", "borrowed"], to: "return_pending" },
  complete_return: { from: ["return_pending"], to: "in_stock" },
  start_repair: { from: ["return_pending"], to: "repairing" },
  finish_repair: { from: ["repairing"], to: "in_stock" },
  retire: { from: ["in_stock", "return_pending", "repairing"], to: "pending_disposal" },
  dispose: { from: ["pending_disposal"], to: "disposed" },
  report_lost: {
    from: ["in_stock", "assigned", "borrowed", "return_pending", "repairing"],
    to: "lost",
  },
  recover_lost: { from: ["lost"], to: "in_stock" },
};

export function assetActionsFor(status: AssetStatus): AssetAction[] {
  return ASSET_ACTIONS.filter((action) => ASSET_TRANSITIONS[action].from.includes(status));
}

export function assertAssetTransition(current: AssetStatus, action: AssetAction): AssetStatus {
  const transition = ASSET_TRANSITIONS[action];
  if (!transition) throw new RangeError(`未知资产动作：${action}`);
  if (!transition.from.includes(current)) {
    throw new RangeError(`资产当前状态 ${current} 不允许执行 ${action}`);
  }
  return transition.to;
}

export const ACCESSORY_STATUSES = ["in_stock", "in_use", "returned", "scrapped"] as const;
export type AccessoryStatus = (typeof ACCESSORY_STATUSES)[number];
export const ACCESSORY_ACTIONS = ["assign", "return", "restock", "scrap"] as const;
export type AccessoryAction = (typeof ACCESSORY_ACTIONS)[number];

const ACCESSORY_TRANSITIONS: Record<
  AccessoryAction,
  { from: AccessoryStatus[]; to: AccessoryStatus }
> = {
  assign: { from: ["in_stock"], to: "in_use" },
  return: { from: ["in_use"], to: "returned" },
  restock: { from: ["returned"], to: "in_stock" },
  scrap: { from: ["returned"], to: "scrapped" },
};

export function assertAccessoryTransition(
  current: AccessoryStatus,
  action: AccessoryAction,
): AccessoryStatus {
  const transition = ACCESSORY_TRANSITIONS[action];
  if (!transition) throw new RangeError(`未知低值易耗动作：${action}`);
  if (!transition.from.includes(current)) {
    throw new RangeError(`低值易耗当前状态 ${current} 不允许执行 ${action}`);
  }
  return transition.to;
}

export const SEAT_STATUSES = ["available", "pending", "allocated", "reclaimed", "revoked"] as const;
export type SeatStatus = (typeof SEAT_STATUSES)[number];
export const SEAT_ACTIONS = [
  "allocate",
  "confirm",
  "cancel",
  "reclaim",
  "reallocate",
  "revoke",
] as const;
export type SeatAction = (typeof SEAT_ACTIONS)[number];

const SEAT_TRANSITIONS: Record<SeatAction, { from: SeatStatus[]; to: SeatStatus }> = {
  allocate: { from: ["available", "reclaimed"], to: "pending" },
  confirm: { from: ["pending"], to: "allocated" },
  cancel: { from: ["pending"], to: "available" },
  reclaim: { from: ["allocated"], to: "reclaimed" },
  reallocate: { from: ["reclaimed"], to: "available" },
  revoke: { from: ["available", "allocated", "reclaimed"], to: "revoked" },
};

export function assertSeatTransition(current: SeatStatus, action: SeatAction): SeatStatus {
  const transition = SEAT_TRANSITIONS[action];
  if (!transition) throw new RangeError(`未知席位动作：${action}`);
  if (!transition.from.includes(current)) {
    throw new RangeError(`席位当前状态 ${current} 不允许执行 ${action}`);
  }
  return transition.to;
}
